import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Skeleton, Typography } from '@one-thd/sui-atomic-components';
import {
  StoreSearch,
  StoreSearchContext,
  SearchInput,
  SuggestedAddresses
} from '@thd-olt-component-react/store-search';

function UsedLocationSearchContent({ selectedStore, onStoresLoaded, searchInput }) {
  const [showSearch, setShowSearch] = useState(!selectedStore);
  const { showError, showLoader, stores = [], suggestedAddresses = [] } = useContext(StoreSearchContext);
  const loadedStoreIdsRef = useRef('');

  useEffect(() => {
    if (!stores.length) {
      return;
    }
    const storeIds = stores.map((store) => store.storeId).join(',');
    if (storeIds === loadedStoreIdsRef.current) {
      return;
    }

    loadedStoreIdsRef.current = storeIds;
    onStoresLoaded?.(stores);
    setShowSearch(false);
  }, [stores, onStoresLoaded]);

  // Hide the search form once a store is confirmed
  useEffect(() => {
    if (selectedStore) {
      setShowSearch(false);
    }
  }, [selectedStore]);

  return (
    <div className="used-location-search">
      {/* Confirmed store display */}
      {!showSearch && selectedStore && (
        <div className="sui-flex sui-items-center sui-gap-2 sui-text-sm sui-mb-2">
          <span>
            <span className="sui-text-subtle">Near: </span>
            <Typography weight="bold" as="span">
              {selectedStore.name} #{selectedStore.storeId}
            </Typography>
          </span>
          <Button
            variant="text"
            size="sm"
            onClick={() => setShowSearch(true)}
          >
            (change)
          </Button>
        </div>
      )}

      {showSearch && (
        <SearchInput hideUseCurrentLocation showStoreDirectoryLink={false} />
      )}

      {showLoader && (
        <div className="sui-mt-3 sui-grid sui-gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="sui-py-1">
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="50%" />
            </div>
          ))}
        </div>
      )}

      {!showLoader && suggestedAddresses?.length > 0 && (
        <div className="sui-mt-2">
          <SuggestedAddresses stores={suggestedAddresses} />
        </div>
      )}

      {showError && !showLoader && suggestedAddresses?.length === 0 && (
        <div className="sui-mt-2">
          <Alert status="error">
            No stores found near {searchInput}. Try a different ZIP code, city, state, or store number.
          </Alert>
        </div>
      )}
    </div>
  );
}

export function UsedLocationSearch({
  searchInput,
  setSearchInput,
  searchCoordinates,
  setSearchCoordinates,
  selectedStore,
  onStoresLoaded
}) {
  return (
    <StoreSearch
      pageSize="10"
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      searchCoordinates={searchCoordinates}
      setSearchCoordinates={setSearchCoordinates}
    >
      <UsedLocationSearchContent
        selectedStore={selectedStore}
        onStoresLoaded={onStoresLoaded}
        searchInput={searchInput}
      />
    </StoreSearch>
  );
}

UsedLocationSearchContent.propTypes = {
  selectedStore: PropTypes.shape({
    storeId: PropTypes.string,
    name: PropTypes.string
  }),
  onStoresLoaded: PropTypes.func,
  searchInput: PropTypes.string.isRequired
};

UsedLocationSearchContent.defaultProps = {
  selectedStore: null,
  onStoresLoaded: null,
};

UsedLocationSearch.propTypes = {
  searchInput: PropTypes.string.isRequired,
  setSearchInput: PropTypes.func.isRequired,
  searchCoordinates: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
  setSearchCoordinates: PropTypes.func.isRequired,
  selectedStore: PropTypes.shape({
    storeId: PropTypes.string,
    name: PropTypes.string
  }),
  onStoresLoaded: PropTypes.func
};

UsedLocationSearch.defaultProps = {
  searchCoordinates: null,
  selectedStore: null,
  onStoresLoaded: null,
};
